// Mirror of the semantic layer in tokens.css for the Ant Design theme, which needs concrete
// colour values to derive its hover/active palettes. Keep both files in sync.
import type { ThemeConfig } from 'antd';
import { theme as antdTheme } from 'antd';

export const fonts = {
  display: "'Archivo', 'IBM Plex Sans', system-ui, sans-serif",
  sans: "'IBM Plex Sans', 'IBM Plex Sans Devanagari', system-ui, -apple-system, sans-serif",
  mono: "'IBM Plex Mono', ui-monospace, 'SFMono-Regular', monospace",
};

const light = {
  action: '#314F9C',
  link: '#314F9C',
  danger: '#C9281A',
  warning: '#E59A0B',
  success: '#1F9D61',
  text: '#141826',
  textSecondary: '#3A4051',
  textMuted: '#616A7D',
  bg: '#F5F6F9',
  surface: '#FFFFFF',
  elevated: '#FFFFFF',
  border: '#E2E5EC',
  borderSecondary: '#ECEEF3',
  fillAlter: '#FAFBFC',
};



export function antdThemeConfig(): ThemeConfig {
  const c = light;
  return {
    cssVar: { key: 'oms' },
    algorithm: antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: c.action,
      colorInfo: c.action,
      colorLink: c.link,
      colorError: c.danger,
      colorWarning: c.warning,
      colorSuccess: c.success,
      colorTextBase: c.text,
      colorText: c.text,
      colorTextSecondary: c.textMuted,
      colorTextTertiary: c.textMuted,
      colorBgBase: c.surface,
      colorBgLayout: c.bg,
      colorBgContainer: c.surface,
      colorBgElevated: c.elevated,
      colorBorder: c.border,
      colorBorderSecondary: c.borderSecondary,
      colorFillAlter: c.fillAlter,
      colorInfoBg: '#F0F4FB',
      colorInfoBorder: '#C3D1EC',
      fontFamily: fonts.sans,
      fontFamilyCode: fonts.mono,
      fontSize: 14,
      fontSizeSM: 13,
      fontSizeLG: 16,
      fontSizeHeading1: 32,
      fontSizeHeading2: 24,
      fontSizeHeading3: 20,
      fontSizeHeading4: 16,
      fontSizeHeading5: 14,
      borderRadiusXS: 4,
      borderRadiusSM: 6,
      borderRadius: 8,
      borderRadiusLG: 12,
      controlHeightSM: 30,
      controlHeight: 36,
      controlHeightLG: 44,
      motionDurationFast: '0.08s',
      motionDurationMid: '0.14s',
      motionDurationSlow: '0.2s',
      motionEaseOut: 'cubic-bezier(0.2, 0, 0, 1)',
      boxShadow: '0 6px 16px rgba(20, 24, 38, 0.08), 0 1px 3px rgba(20, 24, 38, 0.05)',
      boxShadowSecondary: '0 12px 28px rgba(20, 24, 38, 0.12), 0 2px 6px rgba(20, 24, 38, 0.06)',
    },
    components: {
      Button: { fontWeight: 600, primaryShadow: 'none', defaultShadow: 'none', dangerShadow: 'none' },
      Card: { paddingLG: 20, headerBg: 'transparent', headerFontSize: 16 },
      Modal: {
        borderRadiusLG: 16,
        titleFontSize: 18,
        titleLineHeight: 1.35,
        paddingContentHorizontalLG: 24,
        paddingMD: 20,
      },
      Table: {
        borderRadius: 12,
        headerBg: c.fillAlter,
        headerColor: c.textMuted,
        headerSplitColor: 'transparent',
        cellPaddingBlock: 12,
      },
      Tabs: { inkBarColor: c.action, itemSelectedColor: c.action, titleFontSize: 14 },
      Tag: { defaultBg: '#ECEEF3', defaultColor: c.textSecondary },
      Statistic: { contentFontSize: 32 },
      Descriptions: { labelColor: c.textMuted },
      Typography: { titleMarginBottom: 0, titleMarginTop: 0 },
    },
  };
}
